import { Component, ViewEncapsulation, OnInit, OnDestroy, HostBinding } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { Subscription, of, switchMap } from 'rxjs';
import { toString as luceneToString } from 'lucene';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { TranslateModule } from '@ngx-translate/core';
import { NgIf, NgFor } from '@angular/common';

import { OrderService } from '../../services/order.service';
import { FavoriteOrders } from '../../models/favorite-orders';
import { Order } from '../../models/order';
import { OrderBy, OrderByDirection, UNDERSCORE } from '../../../shared/utils/constant';
import { LuceneService } from '../../../shared/services/lucene.service';
import { DimensionPipe } from '../../../shared/pipes/format-dimension.pipe';
import { LoadingSpinnerComponent } from '../../../shared/components/utils/loading-spinner.component';
import { OfflineComponent } from '../../../shared/components/offline/offline.component';

const SUPPLIER_ORDER_NUMBER = 'supplierOrderNumber';
const SUPPLIER_ORDER_ITEM_NUMBER = 'supplierOrderItemNumber';

@Component({
	selector: 'copo-favorite-orders',
	templateUrl: './favorite-orders.component.html',
	styleUrl: './favorite-orders.component.css',
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		NgIf,
		TranslateModule,
		NgFor,
		RouterLink,

		MatButtonModule,
		MatIconModule,
		OfflineComponent,
		LoadingSpinnerComponent,
		DimensionPipe
	]
})
export class FavoriteOrdersComponent implements OnInit, OnDestroy {
	isLoading = false;
	isOffline = false;
	orders: Order [] = [];

	orderSubscription?: Subscription;
	requiredFields: (keyof Order)[] = [
		SUPPLIER_ORDER_NUMBER,
		'supplierOrderItemNumber',
		'customerOrderNumber',
		'customerOrderItemNumber',
		'customerArticleReference',
		'steelgradeDescription',
		'thickness',
		'width',
		'length'
	];

	constructor(
		private readonly orderService: OrderService,
		private readonly router: Router,
		private readonly luceneService: LuceneService,
	) {
		this.isOffline = !navigator.onLine;
	}

	@HostBinding('class.favorite-orders-hidden') get isHidden() {
		return !this.isLoading && !this.isOffline && this.orders.length === 0;
	}

	ngOnInit() {
		this.getFavoriteOrders();
	}

	ngOnDestroy() {
		if (this.orderSubscription) {
			this.orderSubscription.unsubscribe();
		}
	}

	getFavoriteOrders() {
		this.isLoading = true;
		const { requiredFields } = this;
		this.orderSubscription = this.orderService
			.getFavoriteOrders()
			.pipe(switchMap(favoriteOrders => {
				if (favoriteOrders.length === 0) {
					return of({ results: [] });
				}
				return this.orderService.getOrders(
					requiredFields,
					this.createLuceneFilter(favoriteOrders),
					0,
					100,
					new OrderBy('cddWeek', OrderByDirection.ASC),
					false
				);
			}))
			.subscribe({
				next: orders => {
					this.orders = orders.results;
					this.isLoading = false;
				},
				error: (error: unknown) => {
					console.error(error);
					this.isLoading = false;
				},
			});
	}

	getOrderName(order: Order) {
		return order.customerOrderNumber === ''
			? UNDERSCORE
			: `${ order.customerOrderNumber }/${ order.customerOrderItemNumber }`;
	}

	getProductName(order: Order) {
		return order.steelgradeDescription === ''
			? UNDERSCORE
			: order.steelgradeDescription;
	}

	async viewDetails() {
		await this.router.navigateByUrl('/order-tracking/order/datacenter', {
			state: {
				filter: this.createSupplierOrderNumberFilter()
			}
		});
	}

	private createSupplierOrderNumberFilter() {
		return [
			{
				id: SUPPLIER_ORDER_NUMBER,
				values: this.orders.map(order => ({
					reference: order.supplierOrderNumber,
					item: order.supplierOrderItemNumber
				}))
			}
		];
	}

	private createLuceneFilter(favoriteOrders: FavoriteOrders[]) {
		const expressionArray = [];

		for (const order of favoriteOrders) {
			const left = this.luceneService.create(SUPPLIER_ORDER_NUMBER, order.supplierOrderReference);
			const right = this.luceneService.create(SUPPLIER_ORDER_ITEM_NUMBER, order.supplierOrderItem);
			const expression = this.luceneService.concat(left, right, 'AND');
			expressionArray.push(expression);
		}

		if (expressionArray.length === 1) {
			return luceneToString(expressionArray[0]);
		}

		let statusCondition = this.luceneService.concat(
			expressionArray[0],
			expressionArray[1],
			'OR',
			true
		);

		for (let i = 2; i < expressionArray.length; i++) {
			statusCondition = this.luceneService.concat(
				statusCondition,
				expressionArray[i],
				'OR',
				true
			);
		}
		return luceneToString(statusCondition);
	}
}
